import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { ApplyFahrschulePresetRequest } from "../contracts/ApplyFahrschulePresetRequest";
import { ApplyPresetResponse } from "../contracts/ApplyPresetResponse";
import { ChooseOrganizationKindRequest } from "../contracts/ChooseOrganizationKindRequest";
import { OrganizationResponse } from "../contracts/OrganizationResponse";
import { OrganizationKind } from "../enums/OrganizationKind";
import { OrganizationResponseMapper } from "../mappers/OrganizationResponseMapper";
import { Organization } from "../models/Organization";
import { fetchCurrentOrganization } from "./organization-slice-extension";

const organizationMapper = new OrganizationResponseMapper();

/**
 * Applies the Fahrschule preset (Owner/Admin), then reloads the organization so
 * the new kind's vocabulary relabels the app without a page reload.
 */
export const applyFahrschulePreset = createAsyncThunk<
  ApplyPresetResponse,
  ApplyFahrschulePresetRequest,
  { rejectValue: ApiErrorResponse }
>("organization/applyFahrschulePreset", async (request, { dispatch, rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<ApplyPresetResponse>(
      "/organizations/current/presets/fahrschule",
      request
    );
    await dispatch(fetchCurrentOrganization());
    return {
      created: response.data.created ?? [],
      skipped: response.data.skipped ?? [],
      issues: response.data.issues ?? [],
    };
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to set up the driving school"));
  }
});

/** Records the owner's first-run kind choice; Default only marks the question answered. */
export const chooseOrganizationKind = createAsyncThunk<
  Organization,
  OrganizationKind,
  { rejectValue: ApiErrorResponse }
>("organization/chooseKind", async (kind, { rejectWithValue }) => {
  try {
    const request: ChooseOrganizationKindRequest = { kind };
    const response = await axiosInstance.put<OrganizationResponse>("/organizations/current/kind", request);
    return organizationMapper.toOrganization(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to save the kind of school"));
  }
});
